import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { HttpErrorResponse } from "@angular/common/http";
import { ActivatedRoute, Router } from "@angular/router";
import { MessageService } from "primeng/api";
import { UserManagementService } from "../user-management.service";
import { SignupComponent } from "../signup/signup.component";
import { environment } from "src/environments/environment";
import { interval, Subscription } from "rxjs";
import { ModalService } from "src/app/service/modal.service";
import { SessionService } from "src/app/session.service";
import { ForgotpassComponent } from "../forgotpass/forgotpass.component";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.css"],
  standalone: false,
})
export class LoginComponent implements OnInit, OnDestroy {
  private storage = environment.storage;
  private tokenLoginHandled = false;
  loginForm: any;
  otpForm: any;
  loading = 0;
  btnDisabled: any;
  lfcount: any;
  timeLeft: number = 60;
  subscription: Subscription = new Subscription();
  isLogin: boolean = true;
  errorMessage: string = "";
  otpErrorMessage: string = "";
  showOtpDialog: boolean = false;
  isInitiatingLogin: boolean = false;
  isOtpSubmitting: boolean = false;
  isOtpResending: boolean = false;
  otpDestination: string = "";
  otpResendSeconds: number = 0;
  otpResendSubscription: Subscription = new Subscription();
  private pendingMfaChallenge: {
    username: string;
    session: string;
    challengeName: string;
    destination?: string;
  } | null = null;
  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    // private authService: SocialAuthService,
    private sessionService: SessionService,
    private mservice: ModalService,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserManagementService,
  ) {}

  ngOnInit(): void {
    this.btnDisabled = 0;
    this.isLogin = true;
    const str = this.storage.getItem("falied-login-count");
    if (str) {
      const num = parseInt(str, 10);
      if (num > 5) {
        this.btnDisabled = 1;
        this.timeLeft = 60;
        const source = interval(1000);
        this.subscription = source.subscribe(() => {
          this.timeLeft--;

          if (this.timeLeft === 0) {
            this.subscription.unsubscribe();
          }
        });
        setTimeout(() => {
          this.btnDisabled = 0;
          const lfc = "0";
          this.storage.setItem("falied-login-count", lfc);
        }, 60000);
      } else {
        this.btnDisabled = 0;
      }
    }
    this.initForm();
    this.handleSsoFailureFromQueryParams();
    this.handleTokenLoginFromQueryParams();
  }

  /**
   * SsoRedeemComponent sends failed SSO handoffs here with ?sso=error. The
   * reason (invalid / expired / already-used ticket) is deliberately not
   * surfaced — the user just needs to sign in normally.
   */
  private handleSsoFailureFromQueryParams(): void {
    if (this.route.snapshot.queryParams["sso"] === "error") {
      this.errorMessage = "Single sign-on failed. Please sign in to continue.";
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.otpResendSubscription.unsubscribe();
  }

  requestAccess(): void {
    const targetRole = "12"; // Example target role
    this.userService
      .requestTemporaryAccess(targetRole)
      .then((response) => {
        console.log("Temporary access granted:", response);
      })
      .catch((error) => {
        console.error("Error requesting temporary access:", error);
      });
  }

  initForm() {
    this.loginForm = this.fb.group({
      email: ["", Validators.required],
      password: ["", [Validators.required, this.customPasswordValidator]],
    });

    this.otpForm = this.fb.group({
      otp: ["", [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });
  }

  customPasswordValidator(control: any) {
    const forbiddenPattern =
      /aYlNlfdX0'XOR\(if\(now\(\)=sysdate\(\),sleep\(6\),0\)\)XOR'Z/;
    if (control.value && forbiddenPattern.test(control.value)) {
      return { forbiddenPassword: { value: control.value } };
    }
    return null;
  }

  /**
   * Validates returnUrl to prevent open redirect. Only allows relative app paths.
   */
  private getValidReturnUrl(returnUrl: string | undefined): string {
    if (!returnUrl || typeof returnUrl !== 'string') {
      return '/';
    }
    const trimmed = returnUrl.trim();
    if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
      return trimmed;
    }
    return '/';
  }

  private handleTokenLoginFromQueryParams(): void {
    if (this.tokenLoginHandled) {
      return;
    }

    const queryParams = this.route.snapshot.queryParams;
    const authToken = queryParams["authToken"] || queryParams["token"];

    if (!authToken || typeof authToken !== "string") {
      return;
    }

    this.tokenLoginHandled = true;
    this.completeTokenLogin(authToken, queryParams);
  }

  private completeTokenLogin(
    authToken: string,
    queryParams: Record<string, any>,
  ): void {
    try {
      const tokenPayload = this.parseJwtPayload(authToken);
      const user = this.buildTokenLoginUser(tokenPayload, queryParams);
      const siteInfo = this.buildTokenLoginSites(tokenPayload, queryParams);

      if (!user?.id) {
        throw new Error("User id not found in token login payload.");
      }

      // Clear any leftover state from a prior session in this tab before writing
      // the new one, so stale site context can't leak into the new session.
      this.userService.clearCurrentTabSession();

      localStorage.setItem("token", authToken);
      localStorage.setItem(
        "user",
        JSON.stringify({
          ...user,
          allsites: siteInfo,
        }),
      );

      this.userService.addUserDetail(user);
      this.userService.addSiteDetails(siteInfo);
      this.sessionService.markLoginTime();
      this.sessionService.resetSessionTimeout();

      const returnUrl = this.getValidReturnUrl(queryParams["returnUrl"]);
      const targetUrl =
        user.role == "16"
          ? "/user-management-sites"
          : user.role == "13"
            ? "/report-system/report-list/process"
            : returnUrl;

      this.router.navigateByUrl(targetUrl).then(() => {
        window.location.reload();
      });
    } catch (error) {
      console.error("Token login failed:", error);
      this.errorMessage = "Unable to login with the provided token.";
    }
  }

  private parseJwtPayload(token: string): Record<string, any> {
    const tokenParts = token.split(".");
    if (tokenParts.length < 2) {
      throw new Error("Invalid JWT token format.");
    }

    const base64 = tokenParts[1].replace(/-/g, "+").replace(/_/g, "/");
    const normalizedBase64 = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "=",
    );
    const decodedPayload = atob(normalizedBase64);
    const jsonPayload = decodeURIComponent(
      Array.from(decodedPayload)
        .map((char) =>
          `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`,
        )
        .join(""),
    );

    return JSON.parse(jsonPayload);
  }

  private buildTokenLoginUser(
    tokenPayload: Record<string, any>,
    queryParams: Record<string, any>,
  ): Record<string, any> {
    const role =
      tokenPayload["user_role"]?.toString() ||
      queryParams["userRole"]?.toString() ||
      "";

    return {
      id:
        tokenPayload["id"]?.toString() || queryParams["userId"]?.toString() || "",
      email: tokenPayload["email"] || queryParams["email"] || "",
      role,
      user_role: role,
      first_name:
        tokenPayload["first_name"] || queryParams["firstName"] || "",
      corporation_id:
        tokenPayload["corporation_id"]?.toString() ||
        queryParams["corporationId"]?.toString() ||
        "",
      site_id:
        tokenPayload["site_id"]?.toString() ||
        queryParams["siteId"]?.toString() ||
        "",
    };
  }

  private buildTokenLoginSites(
    tokenPayload: Record<string, any>,
    queryParams: Record<string, any>,
  ): Record<string, any>[] {
    const siteId =
      tokenPayload["site_id"]?.toString() || queryParams["siteId"]?.toString();

    if (!siteId) {
      return [];
    }

    return [
      {
        site_id: siteId,
        site_name: queryParams["siteName"] || "",
        corporation_id:
          tokenPayload["corporation_id"]?.toString() ||
          queryParams["corporationId"]?.toString() ||
          "",
        changePassword: false,
      },
    ];
  }

  onLogin() {
    this.errorMessage = "";
    this.otpErrorMessage = "";

    const pas = this.loginForm.get("password").value;
    if (!pas) {
      this.errorMessage = "Password is required.";
      return;
    }

    const username = this.loginForm.get("email").value?.trim();
    if (!username) {
      this.errorMessage = "Username is required.";
      return;
    }

    this.isInitiatingLogin = true;
    this.userService
      .initiateMfaLogin({
        username,
        password: pas,
      })
      .then((response: any) => {
        this.isInitiatingLogin = false;
        this.handleInitiateLoginSuccess(response, username, pas);
      })
      .catch((error) => {
        this.isInitiatingLogin = false;
        if (this.handleAccountLockout(error)) {
          return;
        }
        if (this.shouldFallbackToLegacyLogin(error)) {
          console.log("Falling back to legacy login due to:", error);
        }
        this.executeLegacyLogin(username, pas);
        // this.loginForm.get("password").setValue("");

      });
  }

  submitOtp(): void {
    this.otpErrorMessage = "";

    if (!this.pendingMfaChallenge) {
      this.closeOtpDialog();
      this.errorMessage = "Login session expired. Please try again.";
      return;
    }

    if (this.otpForm.invalid) {
      this.otpForm.markAllAsTouched();
      return;
    }

    this.isOtpSubmitting = true;
    this.userService
      .respondToMfaLogin({
        username: this.pendingMfaChallenge.username,
        session: this.pendingMfaChallenge.session,
        challenge_name: this.pendingMfaChallenge.challengeName,
        challenge_code: this.otpForm.get("otp").value,
      })
      .then((response: any) => {
        this.isOtpSubmitting = false;
        if (!response?.success || !response?.data?.tokens || !response?.data?.user) {
          this.otpErrorMessage =
            response?.message || "OTP verification failed. Please try again.";
          return;
        }

        this.closeOtpDialog();
        this.completeMfaLogin(response);
      })
      .catch((error) => {
        this.isOtpSubmitting = false;
        this.otpErrorMessage = this.extractApiErrorMessage(
          error,
          "OTP verification failed. Please try again.",
        );
      });
  }

  resendOtp(): void {
    this.otpErrorMessage = "";

    if (!this.pendingMfaChallenge || this.isOtpResending || this.otpResendSeconds > 0) {
      return;
    }

    this.isOtpResending = true;
    this.userService
      .resendMfaLogin({
        username: this.pendingMfaChallenge.username,
        session: this.pendingMfaChallenge.session,
      })
      .then((response: any) => {
        this.isOtpResending = false;
        const challenge = response?.data;

        if (!response?.success || !challenge?.session || !challenge?.challenge_name) {
          this.otpErrorMessage =
            response?.message || "Unable to resend OTP. Please try again.";
          return;
        }

        this.pendingMfaChallenge = {
          username: challenge?.user?.username || this.pendingMfaChallenge?.username,
          session: challenge.session,
          challengeName: challenge.challenge_name,
          destination: challenge.destination || this.pendingMfaChallenge?.destination,
        };
        this.otpDestination = challenge.destination || this.otpDestination;
        this.otpForm.reset();
        this.startOtpResendCountdown(challenge.resend_available_at);
      })
      .catch((error) => {
        this.isOtpResending = false;
        this.otpErrorMessage = this.extractApiErrorMessage(
          error,
          "Unable to resend OTP. Please try again.",
        );
      });
  }

  closeOtpDialog(): void {
    if (this.isOtpSubmitting || this.isOtpResending) {
      return;
    }

    this.showOtpDialog = false;
    this.otpForm.reset();
    this.otpErrorMessage = "";
    this.otpDestination = "";
    this.otpResendSeconds = 0;
    this.otpResendSubscription.unsubscribe();
    this.pendingMfaChallenge = null;
  }

  private handleInitiateLoginSuccess(
    response: any,
    username: string,
    password: string,
  ): void {
    const challenge = response?.data;
    if (response?.success && challenge?.tokens && challenge?.user) {
      this.completeMfaLogin(response);
      return;
    }

    if (
      response?.success &&
      challenge?.challenge_required &&
      challenge?.session &&
      challenge?.challenge_name
    ) {
      this.pendingMfaChallenge = {
        username: challenge?.user?.username || username,
        session: challenge.session,
        challengeName: challenge.challenge_name,
        destination: challenge.destination || "",
      };
      this.otpDestination = challenge.destination || "";
      this.startOtpResendCountdown(challenge.resend_available_at);
      this.otpForm.reset();
      this.showOtpDialog = true;
      return;
    }

    if (this.shouldFallbackToLegacyLogin(response)) {
      this.executeLegacyLogin(username, password);
      return;
    }

    this.loginForm.get("password").setValue("");
    this.errorMessage =
      response?.message || "Unable to initiate login. Please try again.";
  }

  private startOtpResendCountdown(resendAvailableAt?: string): void {
    this.otpResendSubscription.unsubscribe();
    this.otpResendSeconds = this.getOtpResendSeconds(resendAvailableAt);

    if (this.otpResendSeconds <= 0) {
      return;
    }

    this.otpResendSubscription = interval(1000).subscribe(() => {
      this.otpResendSeconds = Math.max(0, this.otpResendSeconds - 1);
      if (this.otpResendSeconds === 0) {
        this.otpResendSubscription.unsubscribe();
      }
    });
  }

  private getOtpResendSeconds(resendAvailableAt?: string): number {
    if (!resendAvailableAt) {
      return 0;
    }

    const resendTimestamp = new Date(resendAvailableAt).getTime();
    if (Number.isNaN(resendTimestamp)) {
      return 0;
    }

    return Math.max(0, Math.ceil((resendTimestamp - Date.now()) / 1000));
  }

  private executeLegacyLogin(username: string, password: string): void {
    const request = {
      purpose: "login",
      email: username,
      password,
    };
    this.loginForm.get("password").setValue("");
    this.loginForm.get("email").setValue("");

    this.userService
      .login(request)
      .then((response: any) => {
        console.log("Login successful:", response);
        if (response && response.status == 1 && response.user) {
          if (!this.isUserEnabled(response.user)) {
            this.errorMessage = "User is disabled. Please contact your administrator.";
            return;
          }
          this.loading++;
          const siteInfo = JSON.parse(JSON.stringify(response.user.allsites));
          delete response.user.allsites;
          this.userService.addUserDetail(response.user);
          this.userService.addSiteDetails(siteInfo);
          this.sessionService.resetSessionTimeout();
          if (response.user.role == 16) {
            console.log("Redirecting to UserManagementSitesComponent...");
            this.router.navigate(["/user-management-sites"]).then(() => {
              console.log(
                "Navigation to UserManagementSitesComponent successful.",
              );
            });
          } else if (response.user.role == 13) {
            this.router.navigate(["/report-system/report-list/process"]);
          } else {
            const returnUrl = this.route.snapshot.queryParams["returnUrl"];
            const targetUrl = this.getValidReturnUrl(returnUrl);
            this.router.navigateByUrl(targetUrl).then(() => {
              window.location.reload();
            });
          }
          return;
        }

        this.handleLegacyLoginFailure(response);
      })
      .catch((error) => {
        if (this.handleAccountLockout(error)) {
          return;
        }
        this.loginForm.get("password").setValue("");
        this.errorMessage =
          "Unable to login. Please check your username or password and try again.";
      });
  }

  private completeMfaLogin(response: any): void {
    const tokens = response.data.tokens;
    const user = response.data.user;
    if (!this.isUserEnabled(user)) {
      this.errorMessage = "User is disabled. Please contact your administrator.";
      return;
    }
    const normalizedUser = {
      ...user,
      role: Number(user.role ?? user.role_id ?? 0),
    };
    const siteInfo = JSON.parse(
      JSON.stringify(Array.isArray(normalizedUser.allsites) ? normalizedUser.allsites : []),
    );

    // Clear any leftover state from a prior session in this tab before writing
    // the new one, so a stale 'current-site' can't leak into the new session
    // (a siteless system admin must resolve to no site). Done here — not inside
    // persistMfaAuthenticatedSession — so the token values written just below
    // are not wiped, and the in-app site-switch path stays untouched.
    this.userService.clearCurrentTabSession();

    localStorage.setItem("token", tokens.access_token);
    localStorage.setItem("id_token", tokens.id_token || "");
    localStorage.setItem("refresh_token", tokens.refresh_token || "");
    localStorage.setItem("token_expires_in", String(tokens.expires_in || ""));
    localStorage.setItem("user", JSON.stringify(normalizedUser));
    localStorage.setItem("authPermissions", JSON.stringify(user.permissions || []));
    this.userService.persistMfaAuthenticatedSession(tokens, normalizedUser);

    this.loading++;
    this.loginForm.get("password").setValue("");
    this.loginForm.get("email").setValue("");
    this.sessionService.resetSessionTimeout();

    const role = Number(normalizedUser.role);
    if (role === 16) {
      this.router.navigate(["/user-management-sites"]).then(() => {});
    } else if (role === 13) {
      this.router.navigate(["/report-system/report-list/process"]);
    } else {
      const returnUrl = this.route.snapshot.queryParams["returnUrl"];
      const targetUrl = this.getValidReturnUrl(returnUrl);
      this.router.navigateByUrl(targetUrl).then(() => {
        window.location.reload();
      });
    }
  }

  private handleLegacyLoginFailure(response: any): void {
    this.loginForm.get("password").setValue("");
    this.errorMessage =
      response.message || response.msg || "Invalid login credentials.";

    // Server-side lockout counter: show how many attempts remain before lockout.
    const remaining = response?.data?.remainingAttempts;
    if (typeof remaining === "number" && remaining >= 0) {
      this.errorMessage += ` ${remaining} attempt${
        remaining === 1 ? "" : "s"
      } remaining before your account is locked.`;
    }

    if (response.data && response.data.cntLoginAttempt) {
      const loginFailCount = response.data.cntLoginAttempt;
      this.storage.setItem("falied-login-count", loginFailCount);
      if (loginFailCount) {
        const num = parseInt(loginFailCount, 10);
        if (num > 5) {
          this.btnDisabled = 1;
          this.timeLeft = 60000;
          const source = interval(100000);
          this.subscription = source.subscribe(() => {
            this.timeLeft--;

            if (this.timeLeft === 0) {
              this.subscription.unsubscribe();
            }
          });
          setTimeout(() => {
            this.btnDisabled = 0;
            const lfc = "0";
            this.storage.setItem("falied-login-count", lfc);
          }, 6000000);
        } else {
          this.btnDisabled = 0;
        }
      }
    }
  }

  private shouldFallbackToLegacyLogin(responseOrError: any): boolean {
    const payload =
      responseOrError instanceof HttpErrorResponse
        ? responseOrError.error
        : responseOrError?.error || responseOrError;

    return (
      payload?.errorCode === "UserNotApproved" ||
      payload?.errorCode === "UserNotFoundOrNotApproved" ||
      payload?.message === "User is not approved for login." ||
      payload?.message === "User is not found or not approved for login."
    );
  }

  /**
   * Handle a server-enforced 423 Locked response on the auth endpoints
   * (failed-login lockout). Shows the remaining wait time and disables the
   * login button until it elapses. Returns true if the error was a lockout.
   */
  private handleAccountLockout(error: any): boolean {
    const status =
      error instanceof HttpErrorResponse ? error.status : error?.status;
    const payload =
      error instanceof HttpErrorResponse
        ? error.error
        : error?.error || error;

    if (status !== 423 && payload?.reason !== "account_locked") {
      return false;
    }

    const minutes = Number(payload?.retryAfterMinutes) || 0;
    this.loginForm.get("password")?.setValue("");
    this.errorMessage =
      minutes > 0
        ? `Account temporarily locked after too many failed attempts. Try again in ${minutes} minute${
            minutes === 1 ? "" : "s"
          }.`
        : "Account temporarily locked after too many failed attempts. Please try again later.";

    this.btnDisabled = 1;
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (minutes > 0) {
      setTimeout(() => {
        this.btnDisabled = 0;
      }, minutes * 60 * 1000);
    }
    return true;
  }

  private extractApiErrorMessage(error: any, fallbackMessage: string): string {
    if (error instanceof HttpErrorResponse) {
      return error.error?.message || fallbackMessage;
    }

    return error?.message || fallbackMessage;
  }

  private isUserEnabled(user: any): boolean {
    return user?.status !== false;
  }

  onClickLogin(event: string) {
    if (event == "login") {
      this.isLogin = true;
    } else {
      this.isLogin = false;
    }
  }

  sendToastMessage(messageType: string, title: string, message: string): void {
    this.messageService.add({
      severity: messageType,
      summary: message,
      detail: title,
    });
  }

  signUp(): void {
    this.openSignupModal();
  }

  forgot(): void {
    this.openForgotModal();
  }

  private openSignupModal() {
    const modalRef = this.mservice.open(SignupComponent, {
      options: {
        backdrop: false,
        keyboard: true,
        // See openForgotModal — same phone-width gutters and scrim.
        modalDialogClass: "pb-auth-dialog",
        windowClass: "pb-auth-window",
      },
    });
  }

  private openForgotModal() {
    const modalRef = this.mservice.open(ForgotpassComponent, {
      options: {
        backdrop: false,
        keyboard: true,
        // ng-bootstrap renders .modal-dialog / .modal-content outside the
        // component, so the component's encapsulated styles cannot reach
        // them. This class gives the phone-width rules in styles.css
        // something to target without affecting any other modal.
        modalDialogClass: "pb-auth-dialog",
        // backdrop:false means no scrim element is created, so on a phone
        // the dialog floats on the white login sheet with nothing behind
        // it. This class lets styles.css tint the window itself at phone
        // widths without changing the desktop behaviour.
        windowClass: "pb-auth-window",
      },
    });
  }
}
